import React, { useEffect, useState, useCallback } from 'react';
import api from '../utils/api';
import { Plus, Search, Edit, Printer, Copy, ChevronDown, ChevronUp, X, Pencil, Clock } from 'lucide-react';
import { toast } from 'sonner';

const CoA = () => {
  const [records, setRecords] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [companySettings, setCompanySettings] = useState({});
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showEditDetailsModal, setShowEditDetailsModal] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [editingRecord, setEditingRecord] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [versionInfo, setVersionInfo] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);

  // Build a printable multi-line address from the customer's billing address so
  // the complete address (not just the name) is shown on the CoA.
  const formatAddress = (a) => {
    if (!a) return '';
    if (typeof a === 'string') return a;
    return [
      a.address_line1, a.address_line2,
      [a.city, a.state].filter(Boolean).join(', '),
      [a.pincode, a.country].filter(Boolean).join(' ')
    ].map(s => (s || '').trim()).filter(Boolean).join('\n');
  };

  const emptyForm = {
    customer_id: '', customer_name: '', customer_address: '',
    sku: '', product_name: '', product_size: '', item_code: '',
    batch_number: '', mfg_date: '', expiry_date: '', quantity_text: '',
    prepared_by: '', approved_by: '',
    test_rows: [{ sno: 1, test: '', specification: '', result: '' }],
    test_parameters: {}
  };
  const [formData, setFormData] = useState({ ...emptyForm });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [coaRes, custRes, invRes, settingsRes, usersRes] = await Promise.all([
        api.get('/sales/coa'),
        api.get('/purchase/customers'),
        api.get('/sales/inventory'),
        api.get('/sales/company-settings'),
        api.get('/admin/users')
      ]);
      setRecords(coaRes.data);
      setCustomers(custRes.data);
      setInventory(invRes.data);
      setCompanySettings(settingsRes.data);
      setUsers(usersRes.data);
    } catch (err) { console.error('Failed to fetch:', err); }
    finally { setLoading(false); }
  };

  const checkVersion = useCallback(async (customerId, sku) => {
    if (!customerId || !sku) { setVersionInfo(null); return; }
    try {
      const r = await api.get(`/sales/coa/latest-version?customer_id=${customerId}&sku=${sku}`);
      setVersionInfo(r.data);
      if (r.data.found && r.data.data) {
        const d = r.data.data;
        setFormData(prev => ({
          ...prev,
          product_name: d.product_name || prev.product_name,
          product_size: d.product_size || prev.product_size,
          item_code: d.item_code || prev.item_code,
          test_rows: d.test_rows && d.test_rows.length > 0 ? d.test_rows : prev.test_rows,
          prepared_by: d.prepared_by || prev.prepared_by,
          approved_by: d.approved_by || prev.approved_by,
        }));
      }
    } catch { setVersionInfo(null); }
  }, []);

  const onCustomerChange = (e) => {
    const cid = e.target.value;
    const cust = customers.find(c => c.id === cid);
    const addr = cust ? formatAddress(cust.billing_address) : '';
    setFormData(prev => ({ ...prev, customer_id: cid, customer_name: cust?.name || '', customer_address: addr }));
    checkVersion(cid, formData.sku);
  };

  const onProductChange = (e) => {
    const sku = e.target.value;
    const item = inventory.find(i => i.sku === sku);
    setFormData(prev => ({ ...prev, sku, product_name: item?.name || '', item_code: item?.sku || '' }));
    checkVersion(formData.customer_id, sku);
  };

  const addTestRow = () => {
    setFormData(prev => ({
      ...prev,
      test_rows: [...prev.test_rows, { sno: prev.test_rows.length + 1, test: '', specification: '', result: '' }]
    }));
  };
  const removeTestRow = (idx) => {
    setFormData(prev => ({
      ...prev,
      test_rows: prev.test_rows.filter((_, i) => i !== idx).map((r, i) => ({ ...r, sno: i + 1 }))
    }));
  };
  const updateTestRow = (idx, field, val) => {
    setFormData(prev => {
      const rows = [...prev.test_rows];
      rows[idx] = { ...rows[idx], [field]: val };
      return { ...prev, test_rows: rows };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/sales/coa', formData);
      setShowModal(false);
      setFormData({ ...emptyForm });
      setVersionInfo(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create CoA'); }
  };

  const handleStatusUpdate = async (coaId, action) => {
    try {
      if (action === 'qc_approve') await api.post(`/sales/coa/${coaId}/qc-approve`);
      else if (action === 'qa_approve') await api.post(`/sales/coa/${coaId}/qa-approve`);
      else if (action === 'lock') await api.post(`/sales/coa/${coaId}/lock`);
      else if (action === 'print') await api.post(`/sales/coa/${coaId}/print`);
      else await api.put(`/sales/coa/${coaId}`, { status: action });
      setShowEditModal(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const openEditDetails = (rec) => {
    setEditingRecord(rec);
    setEditForm({
      customer_name: rec.customer_name || '',
      customer_address: rec.customer_address || '',
      product_name: rec.product_name || '',
      product_size: rec.product_size || '',
      item_code: rec.item_code || '',
      batch_number: rec.batch_number || '',
      mfg_date: rec.mfg_date || '',
      expiry_date: rec.expiry_date || '',
      quantity_text: rec.quantity_text || '',
      prepared_by: rec.prepared_by || '',
      approved_by: rec.approved_by || '',
      test_rows: (rec.test_rows && rec.test_rows.length > 0) ? rec.test_rows.map(r => ({ ...r })) : [{ sno: 1, test: '', specification: '', result: '' }]
    });
    setShowEditDetailsModal(true);
  };

  const updateEditTestRow = (idx, field, val) => setEditForm(prev => { const rows = [...prev.test_rows]; rows[idx] = { ...rows[idx], [field]: val }; return { ...prev, test_rows: rows }; });
  const addEditTestRow = () => setEditForm(prev => ({ ...prev, test_rows: [...prev.test_rows, { sno: prev.test_rows.length + 1, test: '', specification: '', result: '' }] }));
  const removeEditTestRow = (idx) => setEditForm(prev => ({ ...prev, test_rows: prev.test_rows.filter((_, i) => i !== idx).map((r, i) => ({ ...r, sno: i + 1 })) }));

  const submitEditDetails = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/sales/coa/${editingRecord.id}`, editForm);
      setShowEditDetailsModal(false);
      setEditForm(null);
      fetchData();
      toast.success('CoA updated successfully');
    } catch (err) {
      const msg = err.response?.status === 403
        ? 'You are not authorized to edit CoA records'
        : (err.response?.data?.detail || 'Failed to update CoA');
      toast.error(msg);
    }
  };

  const printCoA = (rec) => {
    const co = companySettings;
    // Resolve the customer's full billing address from the customer master so the
    // print shows the real address. Older records saved the customer NAME (or a
    // placeholder) into customer_address, which made the name appear twice.
    const cust = customers.find(c => c.id === rec.customer_id) || customers.find(c => c.name === rec.customer_name);
    const storedAddr = (rec.customer_address || '').trim();
    const nameTrim = (rec.customer_name || '').trim();
    const fullAddr = cust ? formatAddress(cust.billing_address) : '';
    const addrToShow = (storedAddr && storedAddr !== nameTrim) ? storedAddr : fullAddr;
    // Generation date (dd/mm/yyyy) printed in the top-right block.
    const d = new Date();
    const genDate = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
    const rows = rec.test_rows || [];
    const testHtml = rows.map(r =>
      `<tr><td style="text-align:center;border:1px solid #000;padding:6px">${r.sno || ''}</td>
       <td style="border:1px solid #000;padding:6px">${r.test || ''}</td>
       <td style="border:1px solid #000;padding:6px">${r.specification || ''}</td>
       <td style="text-align:center;border:1px solid #000;padding:6px">${r.result || ''}</td></tr>`
    ).join('');

    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>CoA - ${rec.coa_number}</title>
    <style>
      @page{size:A4;margin:15mm}body{font-family:'Arial',sans-serif;font-size:12px;margin:0;padding:20px;color:#000}
      .header{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid #000;padding-bottom:10px;margin-bottom:10px}
      .company-name{font-size:22px;font-weight:bold;color:#1a237e}
      .company-info{text-align:right;font-size:10px;line-height:1.5}
      .doc-title{text-align:center;font-size:18px;font-weight:bold;margin:12px 0;text-decoration:underline}
      .info-grid{display:flex;justify-content:space-between;margin:10px 0 16px}
      .info-left,.info-right{width:48%}.info-left p,.info-right p{margin:3px 0;font-size:11px;line-height:1.4}
      table{width:100%;border-collapse:collapse;margin:12px 0}
      th{background:#1a237e;color:#fff;padding:8px;border:1px solid #000;font-size:11px;text-align:center}
      td{font-size:11px}
      .footer{display:flex;justify-content:space-between;margin-top:60px;font-size:12px}
      .footer div{border-top:1px solid #000;padding-top:5px;min-width:150px;text-align:center}
      @media print{button{display:none !important}}
    </style></head><body>
    <div class="header">
      <div><img src="https://hridya.dweb.co.in/hridayalogo.png" alt="logo" style="height:60px; width:auto;" /></div>
      <div class="company-info">${co.address || ''}<br/>${co.email || ''}<br/>${co.website || ''}</div>
    </div>
    <div class="doc-title">Certificate of Analysis</div>
    <div class="info-grid">
      <div class="info-left">
        <p><strong>TO,</strong></p>
        <p style="white-space:pre-line">${rec.customer_name || ''}${addrToShow ? '\n' + addrToShow : ''}</p>
        <p style="margin-top:8px"><strong>Sub:</strong> Submission of Certificate of Analysis - Reg.</p>
      </div>
      <div class="info-right">
        <p><strong>Date:</strong> ${genDate}</p>
        <p><strong>Name of the Product:</strong> ${rec.product_name || ''}</p>
        <p><strong>Size:</strong> ${rec.product_size || ''}</p>
        <p><strong>Mfg. Date:</strong> ${rec.mfg_date || ''}</p>
        <p><strong>Batch No:</strong> ${rec.batch_number || ''}</p>
        <p><strong>Quantity:</strong> ${rec.quantity_text || ''}</p>
        <p><strong>Item Code:</strong> ${rec.item_code || ''}</p>
        ${rec.expiry_date ? `<p><strong>Expiry Date:</strong> ${rec.expiry_date}</p>` : ''}
      </div>
    </div>
    <table>
      <thead><tr><th style="width:8%">S. No.</th><th style="width:30%">Test</th><th style="width:40%">Specification</th><th style="width:22%">Result</th></tr></thead>
      <tbody>${testHtml}</tbody>
    </table>
    <div class="footer">
      <div><strong>Prepared by:</strong><br/>${rec.prepared_by || ''}</div>
      <div><strong>Approved by:</strong><br/>${rec.approved_by || ''}</div>
    </div>
    <button onclick="window.print()" style="margin-top:20px;padding:8px 24px;font-size:13px;cursor:pointer">Print</button>
    </body></html>`);
    w.document.close();
  };

  const getStatusBadge = (status) => {
    const badges = { qc_pending: 'badge-warning', qc_approved: 'badge-info', qa_approved: 'badge-success', locked: 'badge-success', printed: 'badge-info' };
    return `badge ${badges[status] || 'badge-info'}`;
  };

  const filtered = records.filter(r => {
    const s = searchTerm.toLowerCase();
    const matchSearch = !s || (r.coa_number||'').toLowerCase().includes(s) ||
      (r.batch_number||'').toLowerCase().includes(s) || (r.sku||'').toLowerCase().includes(s) ||
      (r.customer_name||'').toLowerCase().includes(s) || (r.product_name||'').toLowerCase().includes(s);
    const matchStatus = statusFilter === 'all' || r.status === statusFilter;
    return matchSearch && matchStatus;
  });

  return (
    <div data-testid="coa-page">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>Certificate of Analysis</h1>
          <span className="badge badge-warning" data-testid="coa-pending-count" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            <Clock size={12} /> {records.filter(r => ['qc_pending', 'qa_pending'].includes(r.status)).length} CoA Pending
          </span>
        </div>
        <button onClick={() => { setFormData({ ...emptyForm }); setVersionInfo(null); setShowModal(true); }} className="btn btn-primary" data-testid="create-coa-btn"><Plus size={16} /> Create CoA</button>
      </div>

      <div className="card mb-4">
        <div className="grid grid-cols-2" style={{ gap: '16px' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <div style={{ position: 'relative' }}>
              <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)' }} />
              <input type="text" className="form-input" placeholder="Search CoA #, batch, customer, product..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ paddingLeft: '40px' }} data-testid="search-input" />
            </div>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <select className="form-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="status-filter">
              <option value="all">All Status</option>
              <option value="qc_pending">QC Pending</option>
              <option value="qc_approved">QC Approved</option>
              <option value="qa_approved">QA Approved</option>
              <option value="locked">Locked</option>
              <option value="printed">Printed</option>
            </select>
          </div>
        </div>
      </div>

      <div className="card">
        {loading ? <div data-testid="loading-indicator">Loading...</div> : (
          <div className="table-container">
            <table>
              <thead><tr><th>CoA #</th><th>Customer</th><th>Product</th><th>Batch</th><th>Ver</th><th>Status</th><th>Date</th><th>Actions</th></tr></thead>
              <tbody>
                {filtered.length === 0 ? <tr><td colSpan={8} style={{ textAlign: 'center', padding: '20px', color: 'var(--text-tertiary)' }}>No CoA records found</td></tr> : filtered.map(rec => (
                  <React.Fragment key={rec.id}>
                    <tr data-testid={`coa-row-${rec.coa_number}`} style={{ cursor: 'pointer' }} onClick={() => setExpandedRow(expandedRow === rec.id ? null : rec.id)}>
                      <td className="font-mono">{rec.coa_number}</td>
                      <td>{rec.customer_name || '-'}</td>
                      <td>{rec.product_name || rec.sku}</td>
                      <td>{rec.batch_number}</td>
                      <td><span className="badge badge-info">v{rec.version || 1}</span></td>
                      <td><span className={getStatusBadge(rec.status)} data-testid={`status-${rec.coa_number}`}>{rec.status.replace(/_/g, ' ')}</span></td>
                      <td className="text-sm">{new Date(rec.created_at).toLocaleDateString()}</td>
                      <td>
                        <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                          {!['locked', 'printed'].includes(rec.status) && (
                            <button onClick={() => openEditDetails(rec)} className="btn btn-ghost btn-sm" title="Edit Details" data-testid={`edit-details-btn-${rec.coa_number}`}><Pencil size={14} /></button>
                          )}
                          <button onClick={() => { setEditingRecord(rec); setShowEditModal(true); }} className="btn btn-ghost btn-sm" title="Status" data-testid={`edit-btn-${rec.coa_number}`}><Edit size={14} /></button>
                          <button onClick={() => printCoA(rec)} className="btn btn-ghost btn-sm" title="Print" data-testid={`print-btn-${rec.coa_number}`}><Printer size={14} /></button>
                          {expandedRow === rec.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        </div>
                      </td>
                    </tr>
                    {expandedRow === rec.id && (
                      <tr><td colSpan={8} style={{ background: 'var(--bg-secondary)', padding: '12px' }}>
                        <div style={{ fontSize: '12px' }}>
                          <div className="grid grid-cols-3" style={{ gap: '12px', marginBottom: '8px' }}>
                            <div><strong>Item Code:</strong> {rec.item_code || '-'}</div>
                            <div><strong>Size:</strong> {rec.product_size || '-'}</div>
                            <div><strong>Mfg Date:</strong> {rec.mfg_date || '-'}</div>
                            <div><strong>Quantity:</strong> {rec.quantity_text || '-'}</div>
                            <div><strong>Prepared by:</strong> {rec.prepared_by || '-'}</div>
                            <div><strong>Approved by:</strong> {rec.approved_by || '-'}</div>
                          </div>
                          {rec.test_rows && rec.test_rows.length > 0 && (
                            <table style={{ fontSize: '11px', marginTop: '8px' }}>
                              <thead><tr><th style={{ padding: '4px 8px' }}>S.No</th><th style={{ padding: '4px 8px' }}>Test</th><th style={{ padding: '4px 8px' }}>Specification</th><th style={{ padding: '4px 8px' }}>Result</th></tr></thead>
                              <tbody>{rec.test_rows.map((r, i) => <tr key={i}><td style={{ padding: '4px 8px' }}>{r.sno}</td><td style={{ padding: '4px 8px' }}>{r.test}</td><td style={{ padding: '4px 8px' }}>{r.specification}</td><td style={{ padding: '4px 8px' }}>{r.result}</td></tr>)}</tbody>
                            </table>
                          )}
                        </div>
                      </td></tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create CoA Modal */}
      {showModal && (
        <div className="modal-overlay" data-testid="coa-modal">
          <div className="modal-content" style={{ maxWidth: '800px', maxHeight: '90vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>Create Certificate of Analysis</h2></div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {versionInfo?.found && (
                  <div style={{ background: '#e8f5e9', border: '1px solid #4caf50', borderRadius: '6px', padding: '10px 14px', marginBottom: '16px', fontSize: '12px' }} data-testid="version-info">
                    <Copy size={14} style={{ display: 'inline', marginRight: '6px' }} />
                    Existing CoA found (v{versionInfo.version}) for this Customer + Product. Data pre-filled. This will save as <strong>v{versionInfo.version + 1}</strong>.
                  </div>
                )}
                <div className="grid grid-cols-2" style={{ gap: '12px' }}>
                  <div className="form-group">
                    <label className="form-label">Customer *</label>
                    <select className="form-select" value={formData.customer_id} onChange={onCustomerChange} required data-testid="customer-select">
                      <option value="">Select Customer...</option>
                      {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Product (SKU) *</label>
                    <select className="form-select" value={formData.sku} onChange={onProductChange} required data-testid="sku-select">
                      <option value="">Select Product...</option>
                      {inventory.map(i => <option key={i.sku} value={i.sku}>{i.sku} - {i.name}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Product Name</label>
                    <input type="text" className="form-input" value={formData.product_name} onChange={(e) => setFormData({ ...formData, product_name: e.target.value })} data-testid="product-name" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Size</label>
                    <input type="text" className="form-input" value={formData.product_size} onChange={(e) => setFormData({ ...formData, product_size: e.target.value })} placeholder="e.g. 475x620 mm" data-testid="product-size" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Item Code</label>
                    <input type="text" className="form-input" value={formData.item_code} onChange={(e) => setFormData({ ...formData, item_code: e.target.value })} data-testid="item-code" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Batch Number *</label>
                    <input type="text" className="form-input" value={formData.batch_number} onChange={(e) => setFormData({ ...formData, batch_number: e.target.value })} required data-testid="batch-input" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Mfg. Date</label>
                    <input type="text" className="form-input" value={formData.mfg_date} onChange={(e) => setFormData({ ...formData, mfg_date: e.target.value })} placeholder="DD/MM/YYYY" data-testid="mfg-date" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Expiry Date</label>
                    <input type="text" className="form-input" value={formData.expiry_date} onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })} placeholder="DD/MM/YYYY" data-testid="expiry-date" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Quantity</label>
                    <input type="text" className="form-input" value={formData.quantity_text} onChange={(e) => setFormData({ ...formData, quantity_text: e.target.value })} placeholder="e.g. 10,000 Nos" data-testid="quantity-text" />
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '20px 0 10px' }}>
                  <h3 style={{ fontSize: '14px', fontWeight: 700, margin: 0 }}>Test Parameters</h3>
                  <button type="button" onClick={addTestRow} className="btn btn-secondary btn-sm" data-testid="add-test-row"><Plus size={12} /> Add Row</button>
                </div>
                <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                  <thead><tr>
                    <th style={{ padding: '6px', width: '50px', textAlign: 'center' }}>S.No</th>
                    <th style={{ padding: '6px' }}>Test</th>
                    <th style={{ padding: '6px' }}>Specification</th>
                    <th style={{ padding: '6px' }}>Result</th>
                    <th style={{ padding: '6px', width: '40px' }}></th>
                  </tr></thead>
                  <tbody>
                    {formData.test_rows.map((row, idx) => (
                      <tr key={idx}>
                        <td style={{ padding: '4px', textAlign: 'center' }}>{row.sno}</td>
                        <td style={{ padding: '4px' }}><input type="text" className="form-input" value={row.test} onChange={(e) => updateTestRow(idx, 'test', e.target.value)} style={{ fontSize: '12px', padding: '4px 8px' }} data-testid={`test-name-${idx}`} /></td>
                        <td style={{ padding: '4px' }}><input type="text" className="form-input" value={row.specification} onChange={(e) => updateTestRow(idx, 'specification', e.target.value)} style={{ fontSize: '12px', padding: '4px 8px' }} data-testid={`test-spec-${idx}`} /></td>
                        <td style={{ padding: '4px' }}><input type="text" className="form-input" value={row.result} onChange={(e) => updateTestRow(idx, 'result', e.target.value)} style={{ fontSize: '12px', padding: '4px 8px' }} data-testid={`test-result-${idx}`} /></td>
                        <td style={{ padding: '4px' }}>{formData.test_rows.length > 1 && <button type="button" onClick={() => removeTestRow(idx)} className="btn btn-ghost btn-sm"><X size={12} color="red" /></button>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="grid grid-cols-2" style={{ gap: '12px', marginTop: '16px' }}>
                  <div className="form-group"><label className="form-label">Prepared by</label><select className="form-select" value={formData.prepared_by} onChange={(e) => setFormData({ ...formData, prepared_by: e.target.value })} data-testid="prepared-by"><option value="">Select User...</option>{users.map(u => <option key={u.id} value={u.name}>{u.name} ({u.email})</option>)}</select></div>
                  <div className="form-group"><label className="form-label">Approved by</label><select className="form-select" value={formData.approved_by} onChange={(e) => setFormData({ ...formData, approved_by: e.target.value })} data-testid="approved-by"><option value="">Select User...</option>{users.map(u => <option key={u.id} value={u.name}>{u.name} ({u.email})</option>)}</select></div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="submit-coa-btn">Create CoA</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Status Update Modal */}
      {showEditModal && editingRecord && (
        <div className="modal-overlay" data-testid="status-modal">
          <div className="modal-content" style={{ maxWidth: '400px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>Update CoA Status</h2></div>
            <div className="modal-body">
              <p style={{ fontSize: '13px', marginBottom: '16px' }}>Current: <strong>{editingRecord.status.replace(/_/g, ' ')}</strong></p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {editingRecord.status === 'qc_pending' && <button className="btn btn-primary" onClick={() => handleStatusUpdate(editingRecord.id, 'qc_approve')} data-testid="approve-qc-btn">QC Approve</button>}
                {editingRecord.status === 'qc_approved' && <button className="btn btn-primary" onClick={() => handleStatusUpdate(editingRecord.id, 'qa_approve')} data-testid="approve-qa-btn">QA Approve</button>}
                {editingRecord.status === 'qa_approved' && <button className="btn btn-primary" onClick={() => handleStatusUpdate(editingRecord.id, 'lock')} data-testid="lock-btn">Lock CoA</button>}
                {editingRecord.status === 'locked' && <button className="btn btn-primary" onClick={() => handleStatusUpdate(editingRecord.id, 'print')} data-testid="mark-printed-btn">Mark Printed</button>}
              </div>
            </div>
            <div className="modal-footer"><button onClick={() => setShowEditModal(false)} className="btn btn-secondary">Close</button></div>
          </div>
        </div>
      )}

      {/* Edit Details Modal (authorized users only — enforced server-side) */}
      {showEditDetailsModal && editForm && editingRecord && (
        <div className="modal-overlay" data-testid="coa-edit-details-modal">
          <div className="modal-content" style={{ maxWidth: '800px', maxHeight: '90vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>Edit CoA {editingRecord.coa_number}</h2></div>
            <form onSubmit={submitEditDetails}>
              <div className="modal-body">
                <div style={{ background: '#fff8e1', border: '1px solid #ffca28', borderRadius: '6px', padding: '8px 12px', marginBottom: '14px', fontSize: '12px' }}>
                  Editing is restricted to authorized users. Locked or printed CoAs cannot be edited.
                </div>
                <div className="grid grid-cols-2" style={{ gap: '12px' }}>
                  <div className="form-group"><label className="form-label">Customer Name</label><input type="text" className="form-input" value={editForm.customer_name} onChange={(e) => setEditForm({ ...editForm, customer_name: e.target.value })} /></div>
                  <div className="form-group"><label className="form-label">Customer Address</label><input type="text" className="form-input" value={editForm.customer_address} onChange={(e) => setEditForm({ ...editForm, customer_address: e.target.value })} /></div>
                  <div className="form-group"><label className="form-label">Product Name</label><input type="text" className="form-input" value={editForm.product_name} onChange={(e) => setEditForm({ ...editForm, product_name: e.target.value })} /></div>
                  <div className="form-group"><label className="form-label">Size</label><input type="text" className="form-input" value={editForm.product_size} onChange={(e) => setEditForm({ ...editForm, product_size: e.target.value })} /></div>
                  <div className="form-group"><label className="form-label">Item Code</label><input type="text" className="form-input" value={editForm.item_code} onChange={(e) => setEditForm({ ...editForm, item_code: e.target.value })} /></div>
                  <div className="form-group"><label className="form-label">Batch Number</label><input type="text" className="form-input" value={editForm.batch_number} onChange={(e) => setEditForm({ ...editForm, batch_number: e.target.value })} /></div>
                  <div className="form-group"><label className="form-label">Mfg. Date</label><input type="text" className="form-input" value={editForm.mfg_date} onChange={(e) => setEditForm({ ...editForm, mfg_date: e.target.value })} placeholder="DD/MM/YYYY" /></div>
                  <div className="form-group"><label className="form-label">Expiry Date</label><input type="text" className="form-input" value={editForm.expiry_date} onChange={(e) => setEditForm({ ...editForm, expiry_date: e.target.value })} placeholder="DD/MM/YYYY" /></div>
                  <div className="form-group"><label className="form-label">Quantity</label><input type="text" className="form-input" value={editForm.quantity_text} onChange={(e) => setEditForm({ ...editForm, quantity_text: e.target.value })} /></div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '16px 0 8px' }}>
                  <h3 style={{ fontSize: '14px', fontWeight: 700, margin: 0 }}>Test Parameters</h3>
                  <button type="button" onClick={addEditTestRow} className="btn btn-secondary btn-sm"><Plus size={12} /> Add Row</button>
                </div>
                <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                  <thead><tr><th style={{ padding: '6px', width: '50px' }}>S.No</th><th style={{ padding: '6px' }}>Test</th><th style={{ padding: '6px' }}>Specification</th><th style={{ padding: '6px' }}>Result</th><th style={{ padding: '6px', width: '40px' }}></th></tr></thead>
                  <tbody>
                    {editForm.test_rows.map((row, idx) => (
                      <tr key={idx}>
                        <td style={{ padding: '4px', textAlign: 'center' }}>{row.sno}</td>
                        <td style={{ padding: '4px' }}><input type="text" className="form-input" value={row.test} onChange={(e) => updateEditTestRow(idx, 'test', e.target.value)} style={{ fontSize: '12px', padding: '4px 8px' }} /></td>
                        <td style={{ padding: '4px' }}><input type="text" className="form-input" value={row.specification} onChange={(e) => updateEditTestRow(idx, 'specification', e.target.value)} style={{ fontSize: '12px', padding: '4px 8px' }} /></td>
                        <td style={{ padding: '4px' }}><input type="text" className="form-input" value={row.result} onChange={(e) => updateEditTestRow(idx, 'result', e.target.value)} style={{ fontSize: '12px', padding: '4px 8px' }} /></td>
                        <td style={{ padding: '4px' }}>{editForm.test_rows.length > 1 && <button type="button" onClick={() => removeEditTestRow(idx)} className="btn btn-ghost btn-sm"><X size={12} color="red" /></button>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="grid grid-cols-2" style={{ gap: '12px', marginTop: '16px' }}>
                  <div className="form-group"><label className="form-label">Prepared by</label><select className="form-select" value={editForm.prepared_by} onChange={(e) => setEditForm({ ...editForm, prepared_by: e.target.value })}><option value="">Select User...</option>{users.map(u => <option key={u.id} value={u.name}>{u.name} ({u.email})</option>)}</select></div>
                  <div className="form-group"><label className="form-label">Approved by</label><select className="form-select" value={editForm.approved_by} onChange={(e) => setEditForm({ ...editForm, approved_by: e.target.value })}><option value="">Select User...</option>{users.map(u => <option key={u.id} value={u.name}>{u.name} ({u.email})</option>)}</select></div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => { setShowEditDetailsModal(false); setEditForm(null); }} className="btn btn-secondary">Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="submit-coa-edit-btn">Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoA;
